import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import useFetchWithAuth from './CommonHooks/useFetchWithAuth';
import { asyncNoop, fixGCSUrl, transformClassesData } from './util';
import { ClassOptionType, EmployeeListType, StudentListType } from './types';
import { errorToast } from './CommonComponent/toast';
import { UserRole } from './globalContant';

interface MainContainerContextType {
  getUpdatedClass: () => Promise<void>;
  classesList: ClassOptionType[];
  userType: string;
  uid: string;
  instituteId: string;
  employeeList: EmployeeListType[];
  studentClass?: string;
  section?: string;
  rollNo?: string;
  instituteAddress?: string;
  instituteName?: string;
  instituteContact?: string;
  instituteEmail?: string;
  instituteWebsite?: string;
  instituteLogo?: string;
  profileLogo?: string;
  userName?: string;
  studentList?: StudentListType[];
  admissionId: string;
  userPermissions: {
    enableAccount?: boolean;
    enableAttendance?: boolean;
    enableClassSchedule?: boolean;
    enableDiaryHomework?: boolean;
    enableExamSchedule?: boolean;
    enableHappenings?: boolean;
    enableMemo?: boolean;
    enableNotes?: boolean;
    enableResult?: boolean;
    enableTeacherSchedule?: boolean;
  };
  assignClass: string | undefined;
  assignSection: string | undefined;
}

const initialValue: MainContainerContextType = {
  getUpdatedClass: asyncNoop,
  classesList: [],
  userType: '',
  uid: '',
  instituteId: '',
  employeeList: [],
  studentClass: undefined,
  section: undefined,
  rollNo: undefined,
  studentList: [],
  admissionId: '',
  userPermissions: {},
  assignClass: '',
  assignSection: '',
};

const MainContainerContext = createContext<MainContainerContextType>(initialValue);

interface MainContainerProviderProps {
  children: ReactNode;
}

const MainContainerProvider: React.FC<MainContainerProviderProps> = ({ children }) => {
  const [classesList, setClassesList] = useState<ClassOptionType[]>([]);
  const [employeeList, setEmployeeList] = useState<EmployeeListType[]>([]);
  const [studentList, setStudentList] = useState<StudentListType[]>([]);
  const { fetchWithAuth } = useFetchWithAuth();
  const accessToken = Cookies.get('accessToken');
  const isMounted = useRef(true);

  const {
    userType,
    uid,
    instituteId,
    studentClass,
    section,
    rollNo,
    instituteAddress,
    instituteName,
    instituteContact,
    instituteEmail,
    instituteWebsite,
    instituteLogo,
    profileLogo,
    userName,
    admissionId,
    userPermissions,
    assignClass,
    assignSection,
  } = {
    admissionId: Cookies.get('admissionId') || '',
    userType: Cookies.get('userType') || '',
    uid: Cookies.get('uid') || '',
    instituteId: Cookies.get('instituteId') || '',
    studentClass: Cookies.get('class'),
    section: Cookies.get('section'),
    rollNo: Cookies.get('rollNo'),
    instituteAddress: Cookies.get('instituteAddress'),
    instituteName: Cookies.get('instituteName'),
    instituteContact: Cookies.get('instituteContact'),
    instituteEmail: Cookies.get('instituteEmail'),
    instituteWebsite: Cookies.get('instituteWebsite'),
    instituteLogo: fixGCSUrl(Cookies.get('instituteLogo') || ''),
    profileLogo: fixGCSUrl(Cookies.get('profileLogo') || ''),
    userName: Cookies.get('userName'),
    userPermissions: Cookies.get('userPermissions'),
    assignClass: Cookies.get('assignClass'),
    assignSection: Cookies.get('assignSection'),
  };

  const getEmployeeList = useCallback(async () => {
    const url = `employee-filter/?userType=${userType}&instituteId=${instituteId}`;
    const { error, data } = await fetchWithAuth(url);
    if (error) {
      errorToast(error);
    } else if (isMounted.current) {
      setEmployeeList(data);
    }
  }, [fetchWithAuth, userType, instituteId]);

  const getStudentList = useCallback(async () => {
    const url = `student-filter/?userType=${userType}&instituteId=${instituteId}`;
    const { error, data } = await fetchWithAuth(url);
    if (error) {
      errorToast(error);
    } else if (isMounted.current) {
      setStudentList(data);
    }
  }, [fetchWithAuth, userType, instituteId]);

  useEffect(() => {
    if (employeeList.length === 0 && accessToken) {
      getEmployeeList();
    }
  }, [getEmployeeList, userType, employeeList.length, accessToken]);

  useEffect(() => {
    if (studentList.length === 0 && accessToken && userType !== UserRole.STUDENT) {
      getStudentList();
    }
  }, [getStudentList, userType, studentList.length, accessToken]);

  const getUpdatedClass = useCallback(async () => {
    const url = `get-classes/?uid=${uid}&userType=${userType}`;
    const { data, error } = await fetchWithAuth(url);
    if (error) {
      console.error('API Error:', error);
    } else if (isMounted.current) {
      setClassesList(transformClassesData(data));
    }
  }, [fetchWithAuth, uid, userType]);

  useEffect(() => {
    isMounted.current = true;
    if (classesList.length === 0 && accessToken) {
      getUpdatedClass();
    }
    return () => {
      isMounted.current = false;
    };
  }, [classesList.length, accessToken, getUpdatedClass]);

  return (
    <MainContainerContext.Provider
      value={{
        getUpdatedClass,
        classesList,
        userType,
        uid,
        instituteId,
        employeeList,
        studentClass,
        section,
        rollNo,
        instituteAddress,
        instituteName,
        instituteContact,
        instituteEmail,
        instituteWebsite,
        instituteLogo,
        userName,
        studentList,
        profileLogo,
        admissionId,
        userPermissions: userPermissions !== 'undefined' && userPermissions !== undefined ? JSON.parse(userPermissions) : undefined,
        assignClass: assignClass !== 'undefined' && assignClass !== undefined ? assignClass : undefined,
        assignSection: assignSection !== 'undefined' && assignSection !== undefined ? assignSection : undefined,
      }}
    >
      {children}
    </MainContainerContext.Provider>
  );
};

export default MainContainerProvider;

export const useMainContainerProvider = () => {
  const context = useContext(MainContainerContext);
  if (!context) {
    throw new Error('useMainContainerProvider must be used within a MainContainerProvider');
  }
  return context;
};
